import type { GridColDef } from '@mui/x-data-grid';
import type { PaymentOverview } from '@vy/api-definition';
import { CUSTOM_FIELD_ENTITIES } from '@vy/custom-fields-types';
import { CONTRACT_PREFIX, CUSTOMER_PREFIX, extractCustomFieldNamesFromLocalStorageConfig } from '@vy/custom-fields-display';
import { isEmpty } from '@vy/array';
import { useMemo } from 'react';
import { getTableColumnsConfig } from '../../../../hooks/use-table-columns-persistence';
import { useCustomFieldDefinitions } from '../../../../hooks/requests/use-custom-field-definitions';
import { createCustomFieldColumns } from '../../../../utils/create-custom-field-columns';
import { extractUniqueCustomFieldNames } from '../../../../utils/extract-unique-custom-field-names';
import { PAYMENTS_COLUMNS } from '../payment-columns';

type UsePaymentsTableColumnsParams = {
  tableName: string;
  items: PaymentOverview[] | undefined;
};

export function usePaymentsTableColumns({
  tableName,
  items,
}: UsePaymentsTableColumnsParams): GridColDef<PaymentOverview>[] {
  const { data: customFieldDefinitionsData } = useCustomFieldDefinitions();

  const definitionCustomNames = useMemo(() => {
    const allDefinitions = customFieldDefinitionsData?.customFieldDefinitions ?? [];
    const customerNames = allDefinitions
      .filter((definition) => definition.vayuEntity === CUSTOM_FIELD_ENTITIES.CUSTOMER)
      .map((definition) => `${CUSTOMER_PREFIX}${definition.vayuCustomFieldName}`);
    const contractNames = allDefinitions
      .filter((definition) => definition.vayuEntity === CUSTOM_FIELD_ENTITIES.CONTRACT)
      .map((definition) => `${CONTRACT_PREFIX}${definition.vayuCustomFieldName}`);
    return [...customerNames, ...contractNames];
  }, [customFieldDefinitionsData]);

  return useMemo(() => {
    const baseFields = new Set(PAYMENTS_COLUMNS.map((column) => column.field));

    const savedConfig = getTableColumnsConfig(tableName);
    const configCustomNames = extractCustomFieldNamesFromLocalStorageConfig(
      savedConfig?.visibility,
      baseFields,
    );

    const dataCustomNames = extractUniqueCustomFieldNames(
      (items ?? []).map((item) => item.customFieldValues),
    );

    const customFieldNames = [
      ...new Set([...configCustomNames, ...dataCustomNames, ...definitionCustomNames]),
    ];

    if (isEmpty(customFieldNames)) {
      return PAYMENTS_COLUMNS;
    }

    const customFieldColumns = createCustomFieldColumns<PaymentOverview>(customFieldNames);

    return [...PAYMENTS_COLUMNS, ...customFieldColumns];
  }, [tableName, items, definitionCustomNames]);
}
